<template>
<div
    class="
        bg-white rounded-[10px] p-8 pb-11 font-Inter shadow-auth-popup
        absolute bottom-6 left-24 w-[318px] min-h-[364px] z-[9999]
    "
    :class="
		showPopupOpenAnimation ? 'animate-headShake' : ''
	"
    v-show="showAuthPopup"
    ref="popupRef"
>
    <div
        class="w-full flex items-center"
        :class="view === VIEWS.default ? 'justify-end' : 'justify-between'"
    >
        <div
            class="text-won-blue flex items-center gap-1 group cursor-pointer"
            @click="() => changeView(VIEWS.default)"
            v-if="view !== VIEWS.default"
        >
            <ArrowLeftIcon
                class="size-4"
            />
            
            <span class="text-[14px] leading-[100%] group-hover:underline underline-offset-2">
                Back
            </span>
        </div>
        
        
        <XMarkIcon
            class="size-6 cursor-pointer"
            @click="authPopupStore.hideShowAuthPopup"
        />
    </div>
    
    <div v-if="view === VIEWS.default">
        <div class="text-won-blue text-center relative mt-6">
            <h2 class="font-semibold text-[24px] leading-[30px]">
                {{ popupText.title }}
            </h2>
            
            <p class="text-[14px] leading-[20px] mt-2">
                {{ popupText.description }}
            </p>
        </div>
        
        <div class="mt-6 text-won-dark-blue">
            <button
                @click="handleSocialLogin"
                type="button"
                class="gap-2"
                :class="socialButtonStyle"
            >
                <GoogleIcon/>
                Log in with Google
            </button>
    
            <button
                @click="() => changeView(VIEWS.login)"
                type="button"
                class="gap-2 mt-4"
                :class="socialButtonStyle"
            >
                <EnvelopeIcon class="size-4"/>
                Log in with Email
            </button>
        </div>
        
        <div class="mt-6">
            <p
                class="text-center text-won-blue mt-4 text-base"
            >
                Don't have an account?
                <span
                    class="text-won-green mt-2 w-fit hover:underline underline-offset-2 cursor-pointer"
                    @click="() => changeView(VIEWS.register)"
                >
                    Sign up
                </span>
            </p>
        </div>
    </div>
    
    <div v-if="view === VIEWS.login">
        <AuthPopupLogin
            @login="loginHandler"
            :loading="loading"
        />
    </div>

    <div v-if="view === VIEWS.register">
        <AuthPopupRegister
            @register="registerHandler"
            :loading="loading"
        />
    </div>
</div> 
</template>

<script setup>
import { ref, shallowRef, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { socialButtonStyle } from "../../utils/styles";
import { formatErrors, popupwindow, validateEmail } from "../../utils/helpers";
import { POPUP_FEATURE_TEXT_MAP } from "../../constants/auto-popup";
import { setAuthState } from "../../composables/auth";
import { useClickOutsideIgnore } from "../../composables/useClickOutsideIgnore";

import { useAuthPopupStore } from "../../store/auth-popup.store";
import { useNotifications } from "../../store/notifications.store";

import AuthPopupRegister from "./components/auth-popup-register.vue";
import AuthPopupLogin from "./components/auth-popup-login.vue";

import GoogleIcon from "../../icons/google.vue";
import { XMarkIcon, EnvelopeIcon, ArrowLeftIcon  } from "@heroicons/vue/24/outline";

const { authenticate, register } = setAuthState();
const { addIgnoreEl } = useClickOutsideIgnore();

const authPopupStore = useAuthPopupStore();
const notificationsStore = useNotifications();
const { showAuthPopup, showPopupOpenAnimation } = storeToRefs(authPopupStore);

const VIEWS = {
    default: 'default',
    login: 'login',
    register: 'register',
}

const router = useRouter();
const route = useRoute();

const popupRef = ref(null);
const view = shallowRef(VIEWS.default);
const loading = shallowRef(false);
const errors = ref({});

const changeView = (newView) => {
    view.value = newView;
}

const loginHandler = async ({ email, password }) => {
    if (!email.length || !password.length) {
        notificationsStore.notify('Enter credentials!', 'warning')
        return;
    }

    loading.value = true;
    
    try {
        const response  = await authenticate({ email, password })
        
        errors.value = {};

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'user_login',
            'user_id': response?.id
        });

        router.go();
    }catch (error) {
        const { data } = error
        
        if (data?.errors) {
            errors.value = formatErrors(data.errors);
        } else {
            notificationsStore.notify(data.message ?? "Invalid request!", "error")
        }
    }finally {
        // reCaptchaGenerated.value = false;
        loading.value = false;
    }
}

const registerHandler = async ({email, password, confirmPassword, first_name, last_name}) => {
    if (!email.length) {
        notificationsStore.notify("Please enter valid email!", "warning");
        return;
    }

    if (!validateEmail(email)) {
        notificationsStore.notify("Please enter valid email!", "warning");
        return;
    }

    if (!password.length) {
        notificationsStore.notify("Please enter valid password!", "warning");
        return;
    }

    if (!confirmPassword.length) {
        notificationsStore.notify('Please enter valid confirm password!', "warning");
        return;
    }

    if (password !== confirmPassword) {
        notificationsStore.notify('Passwords don\'t match!', "warning");
        return;
    }

    loading.value = true;

    register({
        email: email,
        password: password,
        password_confirmation: confirmPassword,
		    first_name,
		    last_name
    })
        .then((res) => {
            notificationsStore.notify('Successfully registered!');
            errors.value = {};

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'register_account',
                'user_id': res?.id
            });

            router.go();
        })
        .catch(({ response: { data } }) => {
	        const errorsObj = data?.errors;

			if(errorsObj != null) {
				const subjects = Object.keys(errorsObj);

				subjects.forEach(subject => {
					errorsObj[subject].forEach((err) => {
						notificationsStore.notify(`${String(subject).charAt(0).toUpperCase() + String(subject).slice(1)} - ${err}`, "error");
					})
				});
			}else{
				notificationsStore.notify(data?.message ?? "Registration failed, please try again!", "error");
			}
        })
        .finally(() => {
            loading.value = false;
        });
}

const handleSocialLogin = () => {
    popupwindow("/google/login", "Google Login", 500, 600);

    window.onmessage = function ({ data: { user, type, newly_registered_user } }) {
        if (type === "social") {
            window.dataLayer = window.dataLayer || [];
            
            if(newly_registered_user) {
                window.dataLayer.push({
                    'event': 'register_account',
                    'user_id': user?.id
                });
            }

            if(!newly_registered_user) {
                window.dataLayer.push({
                    'event': 'user_login',
                    'user_id': user?.id
                });
            }

            setTimeout(() => {
                router.go();
            }, 300);
        }
    }
};

const popupText = computed(() => {
  return POPUP_FEATURE_TEXT_MAP[route.name] || POPUP_FEATURE_TEXT_MAP["resumes.edit"]
})

onMounted(() => {
	addIgnoreEl(popupRef);
})
</script>
